import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bot, Database, Shield, BarChart3, FileText, Cpu, Zap, Layers,
  BookOpen, Globe, ArrowRight, ArrowDown, CheckCircle, Lock,
  GitBranch, Workflow, Hash, Eye
} from 'lucide-react';

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const AGENTS = [
  { name: 'Data Manager', level: 1, tools: 14, desc: 'Profiling, validation, format conversion', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { name: 'NCA Agent', level: 1, tools: 12, desc: 'AUC, Cmax, t½, BE, dose proportionality', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { name: 'Modeler Manager', level: 1, tools: 5, desc: 'Routes to L2 modeling specialists', color: 'text-violet-400', bg: 'bg-violet-500/10' },
  { name: 'PBPK Agent', level: 1, tools: 15, desc: 'Whole-body PBPK, DDI, organ PK', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Statistical Agent', level: 1, tools: 16, desc: 'XGBoost, SHAP, Cox PH, survival', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { name: 'Simulator', level: 1, tools: 14, desc: 'Monte Carlo, virtual populations', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  { name: 'QC Agent', level: 1, tools: 12, desc: '15-check diagnostics suite', color: 'text-red-400', bg: 'bg-red-500/10' },
  { name: 'Report Agent', level: 1, tools: 10, desc: 'FDA/EMA DOCX, labeling', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { name: 'Reg Intel', level: 1, tools: 9, desc: 'RAG search, guidances, checklists', color: 'text-sky-400', bg: 'bg-sky-500/10' },
  { name: 'General', level: 1, tools: 0, desc: 'PK/PD education, help', color: 'text-muted-foreground', bg: 'bg-muted/30' },
];

const L2_AGENTS = [
  { name: 'PopPK Expert', tools: 18, desc: '1/2/3-CMT, covariate analysis, allometric scaling', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  { name: 'PKPD Expert', tools: 14, desc: 'Emax, indirect response, TGI, Friberg', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
  { name: 'E-R Expert', tools: 12, desc: 'Logistic, survival, dose justification', color: 'text-teal-400', bg: 'bg-teal-500/10' },
];

export default function ArchitecturePage() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 sm:pt-20 pb-8 px-4 sm:px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20 mb-4">
              <GitBranch className="w-3 h-3" /> Technical Architecture
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>
              PharmAgent Architecture
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              13 specialized AI agents, 151 deterministic tools, 76 workflow templates — orchestrated through a privacy-first conversational interface.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Design Principles */}
      <section className="py-10 px-4 sm:px-6">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Space Grotesk' }}>Core Design Principles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Cpu, title: 'Agents Decide, Tools Execute', desc: 'LLMs reason about analytical strategy. Deterministic tools (scipy, numpy, XGBoost) perform computation. No hallucinated math. Every result is reproducible.' },
              { icon: Lock, title: 'Schema-Only Privacy', desc: 'SchemaExtractor strips raw data to metadata before every LLM call. Column names, counts, aggregate stats only. Patient data stays local.' },
              { icon: Eye, title: 'Human-in-the-Loop', desc: 'Review gates at scientific decision points: model selection, covariate finalization, report approval. AI augments the analyst, never replaces judgment.' },
              { icon: Hash, title: 'Hash-Chain Audit Trail', desc: 'SHA-256 hashed I/O per tool call, linked in cryptographic chain. Any modification invalidates subsequent entries. Regulatory-grade traceability.' },
            ].map((p, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.08 }}>
                <Card className="card-hover h-full">
                  <CardContent className="pt-5 pb-4">
                    <p.icon className="w-6 h-6 text-primary mb-2" />
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{p.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Hierarchy */}
      <section className="py-10 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-8 text-center" style={{ fontFamily: 'Space Grotesk' }}>Agent Hierarchy</h2>

          {/* L0 */}
          <div className="text-center mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Level 0 — Orchestration</span>
          </div>
          <motion.div {...fade} className="max-w-sm mx-auto mb-4">
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="py-4 px-5 text-center">
                <Bot className="w-7 h-7 text-primary mx-auto mb-1.5" />
                <p className="font-bold text-primary text-lg" style={{ fontFamily: 'Space Grotesk' }}>Supervisor</p>
                <p className="text-xs text-muted-foreground mt-1">Intent classification → Agent routing → Template execution → Error recovery</p>
                <div className="flex justify-center gap-2 mt-3">
                  {['Keyword scoring', 'LLM fallback', 'Template steps'].map(t => (
                    <span key={t} className="text-[9px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{t}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <div className="flex justify-center mb-4"><ArrowDown className="w-5 h-5 text-muted-foreground/30" /></div>

          {/* L1 */}
          <div className="text-center mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Level 1 — Domain Agents (10)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-4">
            {AGENTS.map((a, i) => (
              <motion.div key={a.name} {...fade} transition={{ delay: i * 0.04 }}>
                <Card className="card-hover h-full">
                  <CardContent className="py-3 px-2.5 text-center">
                    <p className={`text-[11px] font-semibold ${a.color}`} style={{ fontFamily: 'Space Grotesk' }}>{a.name}</p>
                    <p className="text-[9px] text-muted-foreground mt-0.5 leading-tight">{a.desc}</p>
                    {a.tools > 0 && <p className="text-[8px] font-mono text-primary/60 mt-1">{a.tools} tools</p>}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center mb-4"><ArrowDown className="w-5 h-5 text-muted-foreground/30" /></div>

          {/* L2 */}
          <div className="text-center mb-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Level 2 — Modeling Specialists (3)</span>
          </div>
          <div className="grid grid-cols-3 gap-2 max-w-xl mx-auto">
            {L2_AGENTS.map((a, i) => (
              <motion.div key={a.name} {...fade} transition={{ delay: i * 0.06 }}>
                <Card className="card-hover">
                  <CardContent className="py-3 px-2.5 text-center">
                    <p className={`text-[11px] font-semibold ${a.color}`} style={{ fontFamily: 'Space Grotesk' }}>{a.name}</p>
                    <p className="text-[9px] text-muted-foreground mt-0.5">{a.desc}</p>
                    <p className="text-[8px] font-mono text-primary/60 mt-1">{a.tools} tools</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Flow */}
      <section className="py-10 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[900px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Space Grotesk' }}>Data Flow: Privacy Architecture</h2>
          <div className="flex flex-col sm:flex-row items-center gap-3 justify-center">
            {[
              { label: 'Raw Dataset', sub: 'CSV/XPT', icon: Database, color: 'bg-muted/30' },
              { label: 'SchemaExtractor', sub: 'Metadata only', icon: Shield, color: 'bg-primary/10' },
              { label: 'LLM (Claude)', sub: 'Reasoning', icon: Bot, color: 'bg-violet-500/10' },
              { label: 'Tools (scipy)', sub: 'Computation', icon: Cpu, color: 'bg-emerald-500/10' },
              { label: 'Widgets', sub: 'Results', icon: BarChart3, color: 'bg-amber-500/10' },
            ].map((step, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.1 }} className="flex items-center gap-2">
                <div className={`${step.color} rounded-xl p-3 text-center min-w-[90px]`}>
                  <step.icon className="w-5 h-5 mx-auto text-foreground/70 mb-1" />
                  <p className="text-[10px] font-semibold">{step.label}</p>
                  <p className="text-[8px] text-muted-foreground">{step.sub}</p>
                </div>
                {i < 4 && <ArrowRight className="w-4 h-4 text-muted-foreground/30 hidden sm:block" />}
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4 max-w-lg mx-auto">
            Raw patient data flows to local tools only. The LLM receives metadata summaries (column names, counts, aggregate statistics). No individual patient records are transmitted.
          </p>
        </div>
      </section>

      {/* PharmState */}
      <section className="py-10 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Space Grotesk' }}>PharmState: The Communication Bus</h2>
          <p className="text-sm text-muted-foreground text-center mb-6 max-w-xl mx-auto">
            Agents never communicate directly. They read from and write to a shared state with 34 typed fields. Each agent has strict write access rules.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { cat: 'Context', fields: ['drug_name', 'therapeutic_area', 'regulatory_target'], icon: Globe },
              { cat: 'Dataset', fields: ['dataset_id', 'dataset_metadata', 'dataset_format'], icon: Database },
              { cat: 'NCA', fields: ['nca_parameters', 'nca_summary'], icon: BarChart3 },
              { cat: 'Modeling', fields: ['model_runs', 'parameter_estimates', 'final_model'], icon: Cpu },
              { cat: 'QC', fields: ['qc_verdict', 'qc_issues', 'diagnostics'], icon: CheckCircle },
              { cat: 'Simulation', fields: ['simulation_results', 'target_attainment'], icon: Zap },
              { cat: 'Reporting', fields: ['report_blocks', 'report_path'], icon: FileText },
              { cat: 'Audit', fields: ['audit_trail', 'artifacts'], icon: Hash },
            ].map((g, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.04 }}>
                <Card className="h-full">
                  <CardContent className="pt-4 pb-3">
                    <g.icon className="w-4 h-4 text-primary mb-1.5" />
                    <p className="text-xs font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{g.cat}</p>
                    {g.fields.map(f => (
                      <p key={f} className="text-[9px] font-mono text-muted-foreground mt-0.5">{f}</p>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Template System */}
      <section className="py-10 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[900px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center" style={{ fontFamily: 'Space Grotesk' }}>Template System: Agents + Tools + Workflows</h2>
          <p className="text-sm text-muted-foreground text-center mb-6 max-w-lg mx-auto">
            Agents have rules (system prompts). Tools do things (computation). Templates define the structure of doing things in a structured sequence.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="card-hover"><CardContent className="pt-5 pb-4 text-center">
              <Bot className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Agents</p>
              <p className="text-xs text-muted-foreground mt-1">System prompts define expertise, reasoning approach, tool selection strategy</p>
              <p className="text-[10px] font-mono text-primary/60 mt-2">13 agents</p>
            </CardContent></Card>
            <Card className="card-hover"><CardContent className="pt-5 pb-4 text-center">
              <Workflow className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Tools</p>
              <p className="text-xs text-muted-foreground mt-1">Deterministic Python functions: scipy, numpy, XGBoost, Plotly</p>
              <p className="text-[10px] font-mono text-primary/60 mt-2">151 tools</p>
            </CardContent></Card>
            <Card className="card-hover"><CardContent className="pt-5 pb-4 text-center">
              <Layers className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Templates</p>
              <p className="text-xs text-muted-foreground mt-1">Ordered steps with agent assignments, parameters, review gates</p>
              <p className="text-[10px] font-mono text-primary/60 mt-2">76 templates</p>
            </CardContent></Card>
          </div>
        </div>
      </section>

      {/* Core Components */}
      <section className="py-10 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Space Grotesk' }}>Core Components</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Shield, title: 'SchemaExtractor', desc: 'Privacy firewall. Produces metadata-only summaries from datasets.', m: '0 patient records exposed' },
              { icon: Cpu, title: 'Computation Engine', desc: 'scipy NCA, XGBoost SHAP, logistic E-R, Monte Carlo simulation.', m: '151 Python tools' },
              { icon: BarChart3, title: 'Plotly Visualizations', desc: 'Spaghetti, VPC, GOF gallery, forest plots, E-R curves, heatmaps.', m: '15+ chart types' },
              { icon: FileText, title: 'DOCX Generator', desc: 'python-docx for regulatory formatting. NCA tables, labeling.', m: 'FDA + EMA templates' },
              { icon: Globe, title: 'Qdrant RAG', desc: 'Vector search over 24 FDA/EMA/ICH/PMDA guidances.', m: '5 agencies' },
              { icon: Hash, title: 'Audit Chain', desc: 'SHA-256 hashed tool calls. Cryptographic chain verification.', m: 'Tamper-proof' },
              { icon: Zap, title: 'SSE Streaming', desc: 'Widgets stream via Server-Sent Events. Real-time rendering.', m: 'Sub-second' },
              { icon: Database, title: 'MongoDB + State', desc: 'PharmState persisted per session. Survives restarts.', m: '34 state fields' },
            ].map((c, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.04 }}>
                <Card className="card-hover h-full">
                  <CardContent className="pt-4 pb-3">
                    <c.icon className="w-5 h-5 text-primary mb-1.5" />
                    <h3 className="font-semibold text-xs" style={{ fontFamily: 'Space Grotesk' }}>{c.title}</h3>
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
                    <p className="text-[9px] font-mono text-primary/60 mt-1.5">{c.m}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[500px] mx-auto text-center">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Explore Further</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
            <Button onClick={() => navigate('/whitepaper')} variant="outline" className="rounded-full h-10 px-5 text-sm"><BookOpen className="w-4 h-4 mr-1.5" /> Read Whitepaper</Button>
            <Button onClick={() => navigate('/signup')} className="rounded-full h-10 px-5 text-sm">Try App <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
